import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface Notification {
  type: 'success' | 'error';
  message: string;
}

export default function ShefuEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [title, setTitle] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [questionImage, setQuestionImage] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [answerImage, setAnswerImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState<'question' | 'answer' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<Notification | null>(null);

  // 追踪原始图片URL和新上传的图片
  const originalQuestionImageRef = useRef<string | null>(null);
  const originalAnswerImageRef = useRef<string | null>(null);
  const newUploadedImagesRef = useRef<Set<string>>(new Set());
  const isSubmittedRef = useRef(false);

  useEffect(() => {
    fetchPuzzleDetail();
  }, [id]);

  // 组件卸载时清理新上传但未使用的图片
  useEffect(() => {
    return () => {
      // 如果已经成功提交,不需要清理
      if (isSubmittedRef.current) {
        return;
      }

      // 清理新上传但未使用的图片
      if (newUploadedImagesRef.current.size > 0) {
        const token = localStorage.getItem('auth-storage');
        const authData = token ? JSON.parse(token) : null;

        newUploadedImagesRef.current.forEach(imageUrl => {
          fetch('/api/upload/image', {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authData?.state?.token}`
            },
            body: JSON.stringify({ imageUrl })
          }).catch(err => {
            console.error('Failed to cleanup image on unmount:', err);
          });
        });
      }
    };
  }, []);

  // 智能压缩图片到目标大小
  const compressImage = async (file: File, targetSizeKB: number = 200): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('无法创建 canvas context'));
            return;
          }

          // 如果原图小于目标大小，直接返回
          if (file.size <= targetSizeKB * 1024) {
            resolve(e.target?.result as string);
            return;
          }

          // 计算合适的尺寸（保持宽高比）
          let width = img.width;
          let height = img.height;
          const maxDimension = 1920; // 最大尺寸

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // 尝试不同的质量级别
          const tryCompress = (quality: number): string => {
            return canvas.toDataURL('image/jpeg', quality);
          };

          // 二分法查找合适的质量
          let minQuality = 0.1;
          let maxQuality = 0.95;
          let bestResult = tryCompress(0.8);

          for (let i = 0; i < 8; i++) {
            const quality = (minQuality + maxQuality) / 2;
            const result = tryCompress(quality);
            const sizeKB = (result.length * 3) / 4 / 1024;

            if (sizeKB <= targetSizeKB) {
              bestResult = result;
              minQuality = quality;
            } else {
              maxQuality = quality;
            }

            // 如果已经足够接近目标，提前退出
            if (Math.abs(sizeKB - targetSizeKB) < targetSizeKB * 0.1) {
              break;
            }
          }

          resolve(bestResult);
        };

        img.onerror = () => reject(new Error('图片加载失败'));
      };

      reader.onerror = () => reject(new Error('文件读取失败'));
    });
  };

  const fetchPuzzleDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/shefu?action=detail&id=${id}`);
      const data = await response.json();

      if (data.success) {
        const puzzle = data.puzzle;
        setTitle(puzzle.title);
        setQuestionText(puzzle.question_text || '');
        setQuestionImage(puzzle.question_image_url);
        setAnswerText(puzzle.answer_text || '');
        setAnswerImage(puzzle.answer_image_url);

        // 记录原始图片URL
        originalQuestionImageRef.current = puzzle.question_image_url;
        originalAnswerImageRef.current = puzzle.answer_image_url;
      } else {
        setNotification({ type: 'error', message: '加载题目失败' });
      }
    } catch (error) {
      console.error('Failed to fetch puzzle detail', error);
      setNotification({ type: 'error', message: '加载题目失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageDelete = async (imageUrl: string, type: 'question' | 'answer') => {
    try {
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      // 静默删除 R2 上的图片（不阻塞 UI）
      fetch('/api/upload/image', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData?.state?.token}`
        },
        body: JSON.stringify({ imageUrl })
      }).catch(err => {
        console.error('Failed to delete image from R2:', err);
        // 静默失败，不影响用户体验
      });

      // 从新上传列表中移除（因为已经手动删除了）
      newUploadedImagesRef.current.delete(imageUrl);

      // 立即清除前端状态
      if (type === 'question') {
        setQuestionImage(null);
      } else {
        setAnswerImage(null);
      }
    } catch (error) {
      console.error('Delete image error', error);
      // 静默失败
    }
  };

  const handleImageUpload = async (file: File, type: 'question' | 'answer') => {
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setNotification({ type: 'error', message: '图片大小不能超过 5MB' });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setNotification({ type: 'error', message: '只能上传图片文件' });
      return;
    }

    // 立即显示加载状态
    setUploading(true);
    setUploadingType(type);
    setNotification(null);

    try {
      // 智能压缩图片
      const compressedBase64 = await compressImage(file);
      const originalSizeKB = (file.size / 1024).toFixed(2);
      const compressedSizeKB = ((compressedBase64.length * 3) / 4 / 1024).toFixed(2);

      console.log(`图片压缩: ${originalSizeKB}KB → ${compressedSizeKB}KB`);

      // Upload to R2
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData?.state?.token}`
        },
        body: JSON.stringify({
          image: compressedBase64,
          filename: file.name
        })
      });

      const data = await response.json();

      if (data.success) {
        // 记录为新上传的图片（如果不是原始图片）
        const isOriginal = (type === 'question' && data.imageUrl === originalQuestionImageRef.current) ||
                          (type === 'answer' && data.imageUrl === originalAnswerImageRef.current);

        if (!isOriginal) {
          newUploadedImagesRef.current.add(data.imageUrl);
        }

        if (type === 'question') {
          setQuestionImage(data.imageUrl);
        } else {
          setAnswerImage(data.imageUrl);
        }
        setNotification({
          type: 'success',
          message: `图片上传成功 (${originalSizeKB}KB → ${compressedSizeKB}KB)`
        });
      } else {
        setNotification({ type: 'error', message: data.message || '图片上传失败' });
      }
    } catch (error) {
      console.error('Upload error', error);
      setNotification({ type: 'error', message: error instanceof Error ? error.message : '图片上传失败，请稍后重试' });
    } finally {
      setUploading(false);
      setUploadingType(null);
      // Clear notification after 3 seconds
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotification(null);

    // Validation
    if (!title.trim()) {
      setNotification({ type: 'error', message: '请输入题目标题' });
      return;
    }

    if (!questionText && !questionImage) {
      setNotification({ type: 'error', message: '题目内容不能为空，请至少提供文字或图片' });
      return;
    }

    if (!answerText && !answerImage) {
      setNotification({ type: 'error', message: '答案内容不能为空，请至少提供文字或图片' });
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      const response = await fetch(`/api/shefu?action=update&id=${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData?.state?.token}`
        },
        body: JSON.stringify({
          title: title.trim(),
          questionText: questionText.trim() || null,
          questionImageUrl: questionImage,
          answerText: answerText.trim() || null,
          answerImageUrl: answerImage
        })
      });

      const data = await response.json();

      if (data.success) {
        // 标记为已提交，不需要清理图片
        isSubmittedRef.current = true;

        setNotification({ type: 'success', message: '射覆题目更新成功' });
        setTimeout(() => {
          navigate(`/academy/shefu/${id}`);
        }, 1500);
      } else {
        console.error('Update failed:', data);
        setNotification({ type: 'error', message: data.message || `更新失败 (${response.status})` });
      }
    } catch (error) {
      console.error('Submit error', error);
      setNotification({ type: 'error', message: `更新失败：${error instanceof Error ? error.message : '请稍后重试'}` });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-2 text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(`/academy/shefu/${id}`)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>返回详情</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">编辑射覆题目</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              题目标题 <span className="text-red-600">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-red-500 focus:border-red-500"
              placeholder="请输入题目标题"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Question */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              题目内容 <span className="text-sm text-gray-500">(文字或图片至少填写一项)</span>
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 mb-2">
                  题目文字
                </label>
                <textarea
                  id="questionText"
                  rows={4}
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-red-500 focus:border-red-500"
                  placeholder="请输入题目描述"
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  题目图片
                </label>
                {uploadingType === 'question' ? (
                  <div className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-3"></div>
                    <p className="text-sm text-gray-600">上传中...</p>
                  </div>
                ) : questionImage ? (
                  <div className="relative">
                    <img src={questionImage} alt="题目图片" className="w-full max-h-96 object-contain rounded-lg" />
                    <button
                      type="button"
                      onClick={() => questionImage && handleImageDelete(questionImage, 'question')}
                      className="absolute top-2 right-2 px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                    >
                      删除
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <PhotoIcon className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">点击上传</span> 或拖拽文件
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG (最大 5MB)</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => e.target.files && handleImageUpload(e.target.files[0], 'question')}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Answer */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              答案内容 <span className="text-sm text-gray-500">(文字或图片至少填写一项)</span>
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="answerText" className="block text-sm font-medium text-gray-700 mb-2">
                  答案文字
                </label>
                <textarea
                  id="answerText"
                  rows={4}
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-red-500 focus:border-red-500"
                  placeholder="请输入答案说明"
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  答案图片
                </label>
                {uploadingType === 'answer' ? (
                  <div className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-3"></div>
                    <p className="text-sm text-gray-600">上传中...</p>
                  </div>
                ) : answerImage ? (
                  <div className="relative">
                    <img src={answerImage} alt="答案图片" className="w-full max-h-96 object-contain rounded-lg" />
                    <button
                      type="button"
                      onClick={() => answerImage && handleImageDelete(answerImage, 'answer')}
                      className="absolute top-2 right-2 px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                    >
                      删除
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <PhotoIcon className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">点击上传</span> 或拖拽文件
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG (最大 5MB)</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => e.target.files && handleImageUpload(e.target.files[0], 'answer')}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          {notification && (
            <div
              className={clsx(
                'p-4 rounded-lg text-sm',
                notification.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
              )}
            >
              {notification.message}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || uploading}
            className="w-full py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? '更新中...' : uploading ? '上传图片中...' : '更新题目'}
          </button>
        </form>
      </div>
    </div>
  );
}
